import {
  ArrowRight,
  ClipboardCheck,
  GraduationCap,
  Sparkles,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { GruppenKennzahlen } from "@/components/gruppen-kennzahlen";
import { PageHeader } from "@/components/page-header";
import { Separator } from "@/components/ui/separator";
import { usePersonas, useTeams, useTenants } from "@/lib/queries";
import { useSessionContext } from "@/lib/session";

import { Link, createFileRoute } from "@tanstack/react-router";
const SAMPLE_FEEDBACKS = [
  {
    id: "f-1",
    persona: "Lena Brandt",
    headline: "Hook gesetzt, aber ohne Bezug zum Anlass",
    dimension: "Terminierung",
    score: "3,2 / 5",
    risk: "Anruf wirkt wie ein Werbeanruf",
    when: "vor 2 Std.",
  },
  {
    id: "f-2",
    persona: "Murat Yildirim",
    headline: "Inflation sauber am Alltag verankert",
    dimension: "Bedarfsanalyse",
    score: "4,5 / 5",
    risk: "—",
    when: "gestern",
  },
  {
    id: "f-3",
    persona: "Sandra Köhler",
    headline: "Von der Schockrate direkt zur Abschlussfrage",
    dimension: "Lösung & Abschluss",
    score: "2,8 / 5",
    risk: "Beitragsobergrenze nie festgelegt",
    when: "gestern",
  },
];

function CoachDashboard() {
  const { tenantId, user } = useSessionContext();
  const { data: tenants = [] } = useTenants();
  const { data: teams = [] } = useTeams();
  const gruppe = teams.find((t) => t.id === user?.teamId);
  const { data: personas = [] } = usePersonas();
  const sparkasse = tenants.find((tenant) => tenant.id === tenantId);

  return (
    <div className="space-y-7">
      <PageHeader
        eyebrow={
          <>
            <ClipboardCheck className="h-3.5 w-3.5" />
            {sparkasse?.name ?? "Sparkasse"} · Coach
          </>
        }
        title="Fachliche Qualitätssicherung"
        description="Stichproben aktueller KI-Feedbacks und Pflege der Sparkassen-eigenen Coaching-Inhalte. Keine Einzelfall-Sicht: Coaches sehen aggregierte Lernfelder und anonymisierte Auszüge."
      />

      <section className="grid gap-3 md:grid-cols-3">
        <Kpi
          icon={<Sparkles className="h-3.5 w-3.5" />}
          label="Stichproben offen"
          value="3"
          hint="zu validierende Feedback-Outputs"
        />
        <Kpi
          icon={<GraduationCap className="h-3.5 w-3.5" />}
          label="Eigene KI-Kunden"
          value={String(personas.filter((p) => p.tenantId === tenantId).length)}
          hint="Sparkassen-spezifische Anpassungen"
        />
        <Kpi
          icon={<ClipboardCheck className="h-3.5 w-3.5" />}
          label="Qualität · 30 Tage"
          value="4,1 / 5"
          hint="Mittlerer Score nach Coach-Review"
        />
      </section>

      {tenantId && user?.teamId ? (
        <GruppenKennzahlen
          tenantId={tenantId}
          teamId={user.teamId}
          titel={gruppe ? `Meine Gruppe · ${gruppe.name}` : "Meine Gruppe"}
        />
      ) : (
        <Card className="p-6">
          <div className="eyebrow">Meine Gruppe</div>
          <p className="mt-1 text-[13.5px] text-muted-foreground">
            Du bist noch keiner Gruppe zugeordnet. Sobald die neue leben
            Plattform dich einer Pilotgruppe zuordnet, siehst du hier deren
            Bearbeitungsstand und Prüfungsergebnisse.
          </p>
        </Card>
      )}

      <Card className="gap-0 overflow-hidden p-0">
        <div className="flex items-center justify-between gap-3 border-b border-[var(--rule)] px-5 py-4">
          <div>
            <div className="eyebrow">Stichproben · letzte Sessions</div>
            <h2 className="mt-1 font-display text-[20px] font-medium tracking-tight">
              KI-Feedbacks zur Validierung
            </h2>
          </div>
          <Link to="/coach/inhalte"
            className="inline-flex items-center gap-1.5 font-bold text-[11.5px] tracking-[0.06em] text-muted-foreground uppercase hover:text-foreground"
          >
            Inhalte pflegen <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <Separator />
        <div className="divide-y divide-[var(--rule)]">
          {SAMPLE_FEEDBACKS.map((f) => (
            <div key={f.id} className="flex flex-wrap items-start gap-4 p-5">
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{f.dimension}</Badge>
                  <span className="text-[12.5px] text-muted-foreground">
                    KI-Kunde · {f.persona}
                  </span>
                  <span className="text-[12.5px] text-muted-foreground">·</span>
                  <span className="text-[12.5px] text-muted-foreground">
                    {f.when}
                  </span>
                </div>
                <div className="font-display text-[16px] leading-snug">
                  {f.headline}
                </div>
                {f.risk !== "—" && (
                  <div className="text-[13.5px] text-muted-foreground">
                    Risiko: {f.risk}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="font-mono tabular-nums text-[14px]">
                  {f.score}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function Kpi({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <Card className="reveal gap-0 border-[var(--rule)] p-5">
      <div className="flex items-center gap-1.5 font-bold text-[11px] tracking-[0.06em] text-muted-foreground uppercase">
        {icon}
        {label}
      </div>
      <div className="tnum mt-3 font-display text-[30px] leading-none font-medium tracking-tight">
        {value}
      </div>
      {hint && (
        <div className="mt-2 text-[12.5px] text-muted-foreground">{hint}</div>
      )}
    </Card>
  );
}

export const Route = createFileRoute("/_app/coach/")({
  component: CoachDashboard,
});
